/**
 * Attendance Page — Modul Kehadiran Tahap 1 (2026-10-10, user request).
 *
 * Tahap 1 strict scope:
 *   - "Pengaturan" BERFUNGSI: CRUD Lokasi, Jadwal, Penugasan.
 *   - 6 tab lain (Absen Saya, Riwayat Saya, Rekap Kehadiran, Keluar &
 *     Tugas Luar, Lembur, Pengajuan & Persetujuan) tampil sebagai
 *     placeholder "Tahap berikutnya" — bukan tombol yang seolah bekerja.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, XCircle, Info, AlertTriangle } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { useSearchParams } from "react-router-dom";
// R232 (2026-10-20, user request): modul Cuti & Izin digabung sebagai tab di
// halaman ini — komponen halaman lama dipakai ulang dalam mode embedded.
import LeaveRequestsPage from "@/pages/LeaveRequestsPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SearchableSelect from "@/components/SearchableSelect";
// R235 (2026-10-20, user request): parser Plus Code/tautan Google Maps +
// daftar zona waktu IANA searchable untuk form lokasi absensi.
import { parseKoordinatInput, timezoneOptions } from "@/lib/geoInput";
import AbsenSayaPanel from "@/components/attendance/AbsenSayaPanel";
import RiwayatSayaPanel from "@/components/attendance/RiwayatSayaPanel";
import PengajuanPersetujuanPanel from "@/components/attendance/PengajuanPersetujuanPanel";
import KeluarTugasLuarPanel from "@/components/attendance/KeluarTugasLuarPanel";
import LemburPanel from "@/components/attendance/LemburPanel";
import ImporLogPanel from "@/components/attendance/ImporLogPanel";
import RekapKehadiranPanel from "@/components/attendance/RekapKehadiranPanel";
import InputStatusStafPanel from "@/components/attendance/InputStatusStafPanel";
import KesiapanPanel from "@/components/attendance/KesiapanPanel";

const TABS = [
  { key: "absen_saya", label: "Absen Saya" },
  { key: "riwayat_saya", label: "Riwayat Saya" },
  // R232: tab Cuti & Izin — menggantikan menu /leaves yang berdiri sendiri.
  { key: "cuti_izin", label: "Cuti & Izin", feature: "leaves" },
  { key: "keluar_tugas_luar", label: "Tugas Luar" },
  { key: "lembur", label: "Lembur" },
  { key: "pengajuan_persetujuan", label: "Pengajuan & Persetujuan" },
  { key: "rekap", label: "Rekap Kehadiran" },
  { key: "impor_mesin", label: "Impor Mesin" },
  // 2026-10-16 (user request): input status kehadiran staf lain — tab hanya
  // tampil bila user punya perm `attendance.enter_for_others` (lihat render).
  { key: "input_status_staf", label: "Input Status Staf" },
  { key: "pengaturan", label: "Pengaturan" },
];

function StageBanner({ label }) {
  return (
    <div className="border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2" data-testid="att-stage-banner">
      <Info size={16} className="text-amber-700 mt-0.5 shrink-0" />
      <div className="text-[11px] text-amber-800">
        <div className="font-semibold uppercase tracking-wider">Tahap berikutnya</div>
        <div className="mt-0.5">
          Halaman <span className="font-semibold">{label}</span> belum berfungsi pada Tahap 1 — struktur data (kejadian absensi,
          ringkasan, koreksi, keluar sementara, lembur, perangkat/identitas eksternal) sudah disiapkan agar dapat diperluas tanpa migrasi besar.
        </div>
      </div>
    </div>
  );
}

function ConfigIncomplete({ missing }) {
  if (!missing?.length) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5" title={`Perlu diisi: ${missing.join(", ")}`}>
      <AlertTriangle size={10} /> Konfigurasi belum lengkap
    </span>
  );
}

function LocationsTab({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/attendance/locations?include_inactive=true").then((r) => setRows(r.data || [])).catch((e) => toast.error(formatDetail(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  const empty = { name: "", kind: "office", address: "", project_ref: "", latitude: "", longitude: "", radius_m: "", max_accuracy_m: "", timezone: "Asia/Jakarta", is_active: true, geo: "", _geoError: null };
  const save = async () => {
    // R235: blokir simpan bila input koordinat tidak terbaca.
    if (editing._geoError) { toast.error(editing._geoError); return; }
    try {
      setSaving(true);
      const payload = { ...editing,
        latitude: editing.latitude === "" ? null : Number(editing.latitude),
        longitude: editing.longitude === "" ? null : Number(editing.longitude),
        radius_m: editing.radius_m === "" ? null : parseInt(editing.radius_m, 10),
        max_accuracy_m: editing.max_accuracy_m === "" ? null : parseInt(editing.max_accuracy_m, 10),
        project_ref: editing.project_ref || null, address: editing.address || null };
      if (editing.id) await api.patch(`/attendance/locations/${editing.id}`, payload);
      else await api.post("/attendance/locations", payload);
      toast.success("Lokasi tersimpan.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Nonaktifkan lokasi ini? Data historis tetap tersimpan.")) return;
    try { await api.delete(`/attendance/locations/${id}`); load(); toast.success("Lokasi dinonaktifkan."); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="att-loc-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Master Lokasi</h3>
        {canWrite && (
          <Button size="sm" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" onClick={() => setEditing(empty)} data-testid="att-loc-add">
            <Plus size={14} /> Tambah Lokasi
          </Button>
        )}
      </div>
      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Nama</th>
              <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Jenis</th>
              <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Koordinat</th>
              <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Radius (m)</th>
              <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Akurasi (m)</th>
              <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Zona Waktu</th>
              <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Status</th>
              <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400 italic">Belum ada lokasi.</td></tr>}
            {rows.map((r) => {
              const miss = ["latitude", "longitude", "radius_m", "max_accuracy_m"].filter((k) => r[k] == null);
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-loc-row-${r.id}`}>
                  <td className="px-3 py-2 font-semibold">{r.name} <ConfigIncomplete missing={miss} /></td>
                  <td className="px-3 py-2 uppercase text-[10px] tracking-wider text-slate-500">{r.kind}</td>
                  <td className="px-3 py-2 tabular-nums text-[10px]">{r.latitude != null ? `${r.latitude}, ${r.longitude}` : <span className="text-slate-400 italic">Perlu diisi</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.radius_m ?? <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.max_accuracy_m ?? <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-[10px]">{r.timezone}</td>
                  <td className="px-3 py-2 text-[10px]">{r.is_active
                    ? <span className="text-emerald-700 font-semibold uppercase tracking-wider">Aktif</span>
                    : <span className="text-slate-400 uppercase tracking-wider">Nonaktif</span>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canWrite && (
                      <>
                        <button type="button" onClick={() => setEditing({ ...r, geo: r.latitude != null ? `${r.latitude}, ${r.longitude}` : "", _geoError: null })} data-testid={`att-loc-edit-${r.id}`} className="p-1 text-slate-500 hover:text-[#0B1D3A]"><Pencil size={12} /></button>
                        {r.is_active && <button type="button" onClick={() => remove(r.id)} data-testid={`att-loc-del-${r.id}`} className="p-1 text-rose-500 hover:text-rose-700"><Trash2 size={12} /></button>}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="border border-[#0B1D3A] bg-white p-4 space-y-2" data-testid="att-loc-form">
          <div className="font-display text-sm text-[#0B1D3A] mb-1">{editing.id ? "Edit Lokasi" : "Tambah Lokasi"}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            <Input placeholder="Nama lokasi *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="att-loc-name" />
            <SearchableSelect value={editing.kind} onChange={(v) => setEditing({ ...editing, kind: v })}
              options={[{ value: "office", label: "Kantor" }, { value: "project", label: "Proyek" }, { value: "workshop", label: "Workshop" }, { value: "other", label: "Lainnya" }]}
              placeholder="Jenis" testid="att-loc-kind" />
            {/* R235 (2026-10-20, user request): zona waktu = dropdown yang
                bisa DICARI (IANA lengkap; Asia/Jakarta/Makassar/Jayapura di
                urutan atas). */}
            <SearchableSelect value={editing.timezone} onChange={(v) => setEditing({ ...editing, timezone: v })}
              options={timezoneOptions()} placeholder="Zona Waktu" searchPlaceholder="Ketik zona waktu…" testid="att-loc-timezone" />
            <Input placeholder="Alamat" value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} className="md:col-span-3" />
            {/* R235: koordinat cukup tempel Plus Code Google Maps / tautan
                Maps / "lat,lng" — diuraikan otomatis (offline, tanpa API).
                Referensi Proyek DIHAPUS dari form (permintaan user). */}
            <div className="md:col-span-2 space-y-1">
              <Input
                placeholder="Koordinat — Plus Code / tautan Google Maps / lat,lng"
                value={editing.geo ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const p = parseKoordinatInput(v);
                  setEditing({
                    ...editing, geo: v,
                    latitude: p.ok ? String(p.latitude) : (v.trim() === "" ? "" : editing.latitude),
                    longitude: p.ok ? String(p.longitude) : (v.trim() === "" ? "" : editing.longitude),
                    _geoError: p.ok ? null : p.error,
                  });
                }}
                data-testid="att-loc-geo" />
              {editing._geoError && <div className="text-[10px] text-rose-700" data-testid="att-loc-geo-error">{editing._geoError}</div>}
              {editing.latitude !== "" && editing.latitude != null && !editing._geoError && (
                <div className="text-[10px] text-emerald-700 tabular-nums" data-testid="att-loc-geo-parsed">
                  Terbaca: <span data-testid="att-loc-lat">{editing.latitude}</span>, <span data-testid="att-loc-lon">{editing.longitude}</span>
                  {" · "}<a className="underline" target="_blank" rel="noreferrer"
                    href={`https://www.google.com/maps/search/?api=1&query=${editing.latitude},${editing.longitude}`}
                    data-testid="att-loc-geo-map">Cek di Google Maps</a>
                </div>
              )}
            </div>
            <Input placeholder="Radius (m)" value={editing.radius_m ?? ""} onChange={(e) => setEditing({ ...editing, radius_m: e.target.value })} />
            <Input placeholder="Batas Akurasi (m)" value={editing.max_accuracy_m ?? ""} onChange={(e) => setEditing({ ...editing, max_accuracy_m: e.target.value })} />
          </div>
          <div className="text-[10px] text-slate-500 italic">Koordinat: tempel Plus Code penuh (mis. 6P58RRCG+WV), tautan Google Maps lengkap, atau &quot;lat,lng&quot;. Field kosong akan ditandai &quot;Perlu diisi&quot;.</div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={saving || !editing.name} className="rounded-none bg-[#0B1D3A]" data-testid="att-loc-save"><Save size={12} /> Simpan</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="rounded-none" data-testid="att-loc-cancel"><XCircle size={12} /> Batal</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SchedulesTab({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/attendance/schedules?include_inactive=true").then((r) => setRows(r.data || [])).catch((e) => toast.error(formatDetail(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  const emptyShifts = Array.from({ length: 7 }, (_, i) => ({ day_of_week: i, start_time: "08:00", end_time: "17:00", break_minutes: 60, is_working_day: i < 5 }));
  const empty = { name: "", description: "", shifts: emptyShifts, late_tolerance_min: 15, effective_from: "", effective_to: "", is_active: true };
  const save = async () => {
    try {
      const payload = { ...editing, effective_from: editing.effective_from || null, effective_to: editing.effective_to || null };
      if (editing.id) await api.patch(`/attendance/schedules/${editing.id}`, payload);
      else await api.post("/attendance/schedules", payload);
      toast.success("Jadwal tersimpan.");
      setEditing(null); load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Nonaktifkan jadwal ini?")) return;
    try { await api.delete(`/attendance/schedules/${id}`); load(); toast.success("Jadwal dinonaktifkan."); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
  return (
    <div className="space-y-3" data-testid="att-sch-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Jadwal Kerja</h3>
        {canWrite && <Button size="sm" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" onClick={() => setEditing(empty)} data-testid="att-sch-add"><Plus size={14} /> Tambah Jadwal</Button>}
      </div>
      <div className="border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Nama</th>
              <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Hari Kerja</th>
              <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Jam</th>
              <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Toleransi</th>
              <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Berlaku</th>
              <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 italic">Belum ada jadwal.</td></tr>}
            {rows.map((s) => {
              const working = (s.shifts || []).filter((x) => x.is_working_day);
              const first = working[0] || {};
              return (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-sch-row-${s.id}`}>
                  <td className="px-3 py-2 font-semibold">{s.name} {!s.is_active && <span className="ml-2 text-[9px] uppercase tracking-wider text-slate-400">Nonaktif</span>}</td>
                  <td className="px-3 py-2 text-[10px]">{working.map((x) => DAYS[x.day_of_week][0]).join(" ")}</td>
                  <td className="px-3 py-2 tabular-nums text-[10px]">{first.start_time || "—"} – {first.end_time || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.late_tolerance_min} min</td>
                  <td className="px-3 py-2 text-[10px]">{s.effective_from || "—"} s/d {s.effective_to || "∞"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canWrite && (<>
                      <button type="button" onClick={() => setEditing(s)} data-testid={`att-sch-edit-${s.id}`} className="p-1 text-slate-500 hover:text-[#0B1D3A]"><Pencil size={12} /></button>
                      {s.is_active && <button type="button" onClick={() => remove(s.id)} data-testid={`att-sch-del-${s.id}`} className="p-1 text-rose-500 hover:text-rose-700"><Trash2 size={12} /></button>}
                    </>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="border border-[#0B1D3A] bg-white p-4 space-y-2" data-testid="att-sch-form">
          <div className="font-display text-sm text-[#0B1D3A]">{editing.id ? "Edit Jadwal" : "Tambah Jadwal"}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Input placeholder="Nama jadwal *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="md:col-span-2" data-testid="att-sch-name" />
            <Input placeholder="Toleransi (menit)" value={editing.late_tolerance_min} onChange={(e) => setEditing({ ...editing, late_tolerance_min: parseInt(e.target.value, 10) || 0 })} />
            <Input placeholder="Berlaku Dari (YYYY-MM-DD)" value={editing.effective_from} onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })} />
            <Input placeholder="Sampai (YYYY-MM-DD, opsional)" value={editing.effective_to || ""} onChange={(e) => setEditing({ ...editing, effective_to: e.target.value })} />
            <Input placeholder="Deskripsi (opsional)" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="md:col-span-3" />
          </div>
          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-[10px]">
              <thead className="bg-slate-50 uppercase tracking-wider text-slate-600"><tr>
                <th className="px-2 py-1 text-left">Hari</th><th className="px-2 py-1 text-left">Kerja</th><th className="px-2 py-1 text-left">Mulai</th><th className="px-2 py-1 text-left">Selesai</th><th className="px-2 py-1 text-right">Istirahat (menit)</th>
              </tr></thead>
              <tbody>
                {editing.shifts.map((sh, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-semibold">{DAYS[sh.day_of_week]}</td>
                    <td className="px-2 py-1"><input type="checkbox" checked={sh.is_working_day} onChange={(e) => { const n = [...editing.shifts]; n[i] = { ...sh, is_working_day: e.target.checked }; setEditing({ ...editing, shifts: n }); }} /></td>
                    <td className="px-2 py-1"><Input value={sh.start_time || ""} onChange={(e) => { const n = [...editing.shifts]; n[i] = { ...sh, start_time: e.target.value }; setEditing({ ...editing, shifts: n }); }} disabled={!sh.is_working_day} className="h-7 text-[10px]" /></td>
                    <td className="px-2 py-1"><Input value={sh.end_time || ""} onChange={(e) => { const n = [...editing.shifts]; n[i] = { ...sh, end_time: e.target.value }; setEditing({ ...editing, shifts: n }); }} disabled={!sh.is_working_day} className="h-7 text-[10px]" /></td>
                    <td className="px-2 py-1 text-right"><Input value={sh.break_minutes} onChange={(e) => { const n = [...editing.shifts]; n[i] = { ...sh, break_minutes: parseInt(e.target.value, 10) || 0 }; setEditing({ ...editing, shifts: n }); }} disabled={!sh.is_working_day} className="h-7 text-[10px] text-right" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={!editing.name} className="rounded-none bg-[#0B1D3A]" data-testid="att-sch-save"><Save size={12} /> Simpan</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="rounded-none" data-testid="att-sch-cancel"><XCircle size={12} /> Batal</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AssignmentsTab({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [locs, setLocs] = useState([]);
  const [schs, setSchs] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const [a, u, l, s] = await Promise.all([
        api.get("/attendance/assignments"),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/attendance/locations"),
        api.get("/attendance/schedules"),
      ]);
      setRows(a.data || []); setUsers(u.data || []); setLocs(l.data || []); setSchs(s.data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const empty = { user_id: "", location_id: "", schedule_id: "", effective_from: "", effective_to: "", is_temporary: false, note: "", require_photo_in: true, require_photo_out: false };
  const save = async () => {
    try {
      const payload = { ...editing, effective_to: editing.effective_to || null, note: editing.note || null,
        reviewer_user_id: editing.reviewer_user_id || null };
      if (editing.id) await api.patch(`/attendance/assignments/${editing.id}`, payload);
      else await api.post("/attendance/assignments", payload);
      toast.success("Penugasan tersimpan.");
      setEditing(null); load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Akhiri penugasan ini (effective_to = hari ini)? Riwayat tetap tersimpan.")) return;
    try { await api.delete(`/attendance/assignments/${id}`); load(); toast.success("Penugasan diakhiri."); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="att-asg-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Penugasan Karyawan</h3>
        {canWrite && <Button size="sm" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" onClick={() => setEditing(empty)} data-testid="att-asg-add"><Plus size={14} /> Tambah Penugasan</Button>}
      </div>
      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr>
            <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Karyawan</th>
            <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Lokasi</th>
            <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Jadwal</th>
            <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Berlaku</th>
            <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Jenis</th>
            <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Aksi</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 italic">Belum ada penugasan.</td></tr>}
            {rows.map((a) => {
              const loc = locs.find((l) => l.id === a.location_id); const sch = schs.find((s) => s.id === a.schedule_id);
              return (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-asg-row-${a.id}`}>
                  <td className="px-3 py-2 font-semibold">{a.user_name || a.user_id}</td>
                  <td className="px-3 py-2">{loc?.name || <span className="text-slate-400 italic">(nonaktif)</span>}</td>
                  <td className="px-3 py-2">{sch?.name || <span className="text-slate-400 italic">(nonaktif)</span>}</td>
                  <td className="px-3 py-2 text-[10px]">{a.effective_from} s/d {a.effective_to || "∞"}</td>
                  <td className="px-3 py-2 text-[10px]">{a.is_temporary ? <span className="text-amber-700 font-semibold uppercase tracking-wider">Sementara</span> : <span className="text-slate-500">Tetap</span>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canWrite && (<>
                      <button type="button" onClick={() => setEditing(a)} data-testid={`att-asg-edit-${a.id}`} className="p-1 text-slate-500 hover:text-[#0B1D3A]"><Pencil size={12} /></button>
                      <button type="button" onClick={() => remove(a.id)} data-testid={`att-asg-del-${a.id}`} className="p-1 text-rose-500 hover:text-rose-700"><Trash2 size={12} /></button>
                    </>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="border border-[#0B1D3A] bg-white p-4 space-y-2" data-testid="att-asg-form">
          <div className="font-display text-sm text-[#0B1D3A]">{editing.id ? "Edit Penugasan" : "Tambah Penugasan"}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <SearchableSelect value={editing.user_id} onChange={(v) => setEditing({ ...editing, user_id: v })}
              options={users.map((u) => ({ value: u.id, label: `${u.name} — ${u.division || "—"}` }))}
              placeholder="Pilih karyawan…" searchPlaceholder="Cari nama / divisi…" emptyText="Karyawan tidak ditemukan" testid="att-asg-user" />
            <SearchableSelect value={editing.location_id} onChange={(v) => setEditing({ ...editing, location_id: v })}
              options={locs.filter((l) => l.is_active).map((l) => ({ value: l.id, label: l.name }))}
              placeholder="Pilih lokasi…" searchPlaceholder="Cari lokasi…" emptyText="Lokasi tidak ditemukan" testid="att-asg-loc" />
            <SearchableSelect value={editing.schedule_id} onChange={(v) => setEditing({ ...editing, schedule_id: v })}
              options={schs.filter((s) => s.is_active).map((s) => ({ value: s.id, label: s.name }))}
              placeholder="Pilih jadwal…" searchPlaceholder="Cari jadwal…" emptyText="Jadwal tidak ditemukan" testid="att-asg-sch" />
            <Input placeholder="Berlaku Dari (YYYY-MM-DD) *" value={editing.effective_from} onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })} data-testid="att-asg-from" />
            <Input placeholder="Sampai (opsional)" value={editing.effective_to || ""} onChange={(e) => setEditing({ ...editing, effective_to: e.target.value })} />
            <label className="inline-flex items-center gap-2 text-[11px]">
              <input type="checkbox" checked={editing.is_temporary} onChange={(e) => setEditing({ ...editing, is_temporary: e.target.checked })} />
              Penugasan sementara
            </label>
            <label className="inline-flex items-center gap-2 text-[11px]" data-testid="att-asg-photo-in">
              <input type="checkbox" checked={!!editing.require_photo_in} onChange={(e) => setEditing({ ...editing, require_photo_in: e.target.checked })} />
              Wajib foto saat Absen Masuk
            </label>
            <label className="inline-flex items-center gap-2 text-[11px]" data-testid="att-asg-photo-out">
              <input type="checkbox" checked={!!editing.require_photo_out} onChange={(e) => setEditing({ ...editing, require_photo_out: e.target.checked })} />
              Wajib foto saat Absen Pulang
            </label>
            <label className="inline-flex items-center gap-2 text-[11px]" data-testid="att-asg-preauth">
              <input type="checkbox" checked={!!editing.preauthorized_offsite} onChange={(e) => setEditing({ ...editing, preauthorized_offsite: e.target.checked })} />
              Otorisasi tugas luar (tanpa persetujuan berulang)
            </label>
            <label className="inline-flex items-center gap-2 text-[11px]" data-testid="att-asg-photo-break">
              <input type="checkbox" checked={!!editing.require_photo_break} onChange={(e) => setEditing({ ...editing, require_photo_break: e.target.checked })} />
              Wajib foto saat Mulai/Kembali sesi keluar
            </label>
            <Input placeholder="Catatan (opsional)" value={editing.note || ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} className="md:col-span-3" />
            <SearchableSelect value={editing.reviewer_user_id || ""} onChange={(v) => setEditing({ ...editing, reviewer_user_id: v || null })}
              options={[{ value: "", label: "— Tanpa pemeriksa khusus (pakai default global) —" }, ...users.filter((u) => u.id !== editing.user_id).map((u) => ({ value: u.id, label: `${u.name} — ${u.division || "—"}` }))]}
              placeholder="Pemeriksa koreksi absensi (opsional)…" searchPlaceholder="Cari nama / divisi…" emptyText="User tidak ditemukan" testid="att-asg-reviewer" />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={!editing.user_id || !editing.location_id || !editing.schedule_id || !editing.effective_from} className="rounded-none bg-[#0B1D3A]" data-testid="att-asg-save"><Save size={12} /> Simpan</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="rounded-none" data-testid="att-asg-cancel"><XCircle size={12} /> Batal</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CorrectionsSettingsTab({ canWrite }) {
  const [st, setSt] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([api.get("/attendance/settings"), api.get("/users").catch(() => ({ data: [] }))]);
      setSt(s.data); setUsers(u.data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!st) return <div className="p-4 text-xs text-slate-500">Memuat pengaturan…</div>;
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/attendance/settings", {
        default_reviewer_user_id: st.default_reviewer_user_id || null,
        evidence_retention_days: st.evidence_retention_days ? parseInt(st.evidence_retention_days, 10) : null,
        personal_break_counts_as_leave: !!st.personal_break_counts_as_leave,
      });
      toast.success("Pengaturan tersimpan.");
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3" data-testid="att-corr-settings">
      <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Koreksi &amp; Bukti</h3>
      <div className="border border-slate-200 bg-white p-4 space-y-3 text-xs max-w-2xl">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Pemeriksa Default (fallback bila penugasan tidak mengisi pemeriksa)</div>
          <SearchableSelect value={st.default_reviewer_user_id || ""} onChange={(v) => setSt({ ...st, default_reviewer_user_id: v || null })}
            options={[{ value: "", label: "— Belum diatur —" }, ...users.map((u) => ({ value: u.id, label: `${u.name} — ${u.division || "—"}` }))]}
            placeholder="Pilih pemeriksa default…" searchPlaceholder="Cari nama…" emptyText="User tidak ditemukan" testid="att-set-reviewer" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Retensi Bukti (hari) — kosongkan untuk simpan selamanya</div>
          <Input type="number" min={1} max={3650} placeholder="mis. 365" value={st.evidence_retention_days ?? ""}
            onChange={(e) => setSt({ ...st, evidence_retention_days: e.target.value })} className="max-w-[200px]" data-testid="att-set-retention" />
          <div className="mt-1 text-[10px] text-slate-500 italic">
            Pengaturan ini hanya konfigurasi retensi. Tidak ada penghapusan otomatis data existing saat peluncuran;
            kebijakan penghapusan akan diumumkan sebelum diberlakukan.
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-[11px]" data-testid="att-set-personal-leave">
          <input type="checkbox" checked={!!st.personal_break_counts_as_leave}
            onChange={(e) => setSt({ ...st, personal_break_counts_as_leave: e.target.checked })} />
          Akui sesi keluar pribadi sebagai izin administratif (tanpa pemotongan gaji otomatis)
        </label>
        {canWrite && (
          <Button size="sm" onClick={save} disabled={saving} className="rounded-none bg-[#0B1D3A]" data-testid="att-set-save">
            <Save size={12} /> Simpan Pengaturan
          </Button>
        )}
      </div>
    </div>
  );
}

function SettingsTab({ meta }) {
  const [sub, setSub] = useState("lokasi");
  const canWrite = !!meta?.can_configure;
  return (
    <div className="space-y-3">
      {/* R233: panel Kesiapan Absensi — 7 cek data nyata; tombol "Atur"
          melompat ke sub-bagian pengaturan yang sesuai; status ikut
          diperbarui saat user berpindah sub-bagian (refreshKey=sub). */}
      <KesiapanPanel canWrite={canWrite} onGoSection={setSub} refreshKey={sub} />
      {!canWrite && (
        <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
          Anda hanya memiliki akses lihat. Permission <code className="bg-white px-1">attendance.create/update/delete</code> diperlukan untuk mengubah konfigurasi.
        </div>
      )}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {[["lokasi", "Lokasi"], ["jadwal", "Jadwal Kerja"], ["penugasan", "Penugasan"], ["koreksi", "Koreksi & Bukti"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setSub(k)} data-testid={`att-sub-${k}`}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 whitespace-nowrap ${sub === k ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{l}</button>
        ))}
      </div>
      {sub === "lokasi" && <LocationsTab canWrite={canWrite} />}
      {sub === "jadwal" && <SchedulesTab canWrite={canWrite} />}
      {sub === "penugasan" && <AssignmentsTab canWrite={canWrite} />}
      {sub === "koreksi" && <CorrectionsSettingsTab canWrite={canWrite} />}
    </div>
  );
}

export default function AttendancePage() {
  const { user } = useAuth();  // guard login
  const { canAccess } = useAccess();
  // R232: tab tersinkron dgn URL (?tab=…) supaya redirect /leaves →
  // ?tab=cuti_izin dan deep-link antar halaman bekerja.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "absen_saya";
  const setTab = (k) => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set("tab", k); return n; }, { replace: true });
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  // R233: ringkasan kesiapan absensi di header halaman (chip → tab Pengaturan).
  const [readiness, setReadiness] = useState(null);
  useEffect(() => {
    api.get("/attendance/meta").then((r) => setMeta(r.data)).catch((e) => setErr(formatDetail(e.response?.data?.detail) || "Tidak ada akses modul Kehadiran."));
    api.get("/attendance/readiness").then((r) => setReadiness(r.data)).catch(() => {});
  }, []);
  const stageLabel = useMemo(() => meta?.pages?.[tab] || "Tahap berikutnya", [meta, tab]);
  if (err) return <div className="p-6 text-[11px] text-rose-700">{err}</div>;
  // R232: tab mengikuti hak akses masing-masing user — cuti_izin hanya untuk
  // pemegang akses leaves; input_status_staf mengikuti perm khusus.
  const visibleTabs = TABS.filter((t) =>
    (t.key !== "input_status_staf" || !!meta?.can_enter_for_others) &&
    (!t.feature || canAccess(user, t.feature)));
  return (
    <div className="space-y-4 p-4" data-testid="attendance-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl tracking-tight text-[#0B1D3A]">Kehadiran &amp; Perizinan</h1>
          <p className="text-[11px] text-slate-500 mt-1">
            Absensi, riwayat, cuti &amp; izin, tugas luar, lembur, pengajuan, dan rekap kehadiran dalam satu halaman.
          </p>
        </div>
        {/* R233: ringkasan kesiapan (detail & perbaikan tetap di tab
            Pengaturan — chip ini hanya pintasan). */}
        {readiness && (
          <button type="button" data-testid="att-readiness-chip" onClick={() => setTab("pengaturan")}
            title="Buka tab Pengaturan untuk melihat rincian kesiapan"
            className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-[10px] uppercase tracking-wider font-semibold ${readiness.overall_ready ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
            {readiness.overall_ready ? "Kesiapan: Siap Digunakan" : `Kesiapan: Konfigurasi Belum Lengkap (${readiness.ready_count}/${readiness.total_checks})`}
          </button>
        )}
      </div>
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="att-tabs">
        {visibleTabs.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`att-tab-${t.key}`}
            className={`px-3 py-2 text-[11px] uppercase tracking-wider font-semibold border-b-2 whitespace-nowrap ${tab === t.key ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div data-testid={`att-pane-${tab}`}>
        {tab === "pengaturan" && <SettingsTab meta={meta} />}
        {tab === "absen_saya" && <AbsenSayaPanel />}
        {tab === "riwayat_saya" && <RiwayatSayaPanel />}
        {tab === "pengajuan_persetujuan" && <PengajuanPersetujuanPanel />}
        {tab === "keluar_tugas_luar" && <KeluarTugasLuarPanel />}
        {tab === "lembur" && <LemburPanel />}
        {tab === "impor_mesin" && <ImporLogPanel meta={meta} />}
        {tab === "rekap" && <RekapKehadiranPanel meta={meta} />}
        {tab === "input_status_staf" && <InputStatusStafPanel />}
        {tab === "cuti_izin" && <LeaveRequestsPage embedded />}
        {!["pengaturan", "absen_saya", "riwayat_saya", "pengajuan_persetujuan", "keluar_tugas_luar", "lembur", "impor_mesin", "rekap", "input_status_staf", "cuti_izin"].includes(tab) && <StageBanner label={TABS.find((t) => t.key === tab)?.label} />}
      </div>
      {meta && (
        <div className="mt-6 border-t border-slate-100 pt-3 text-[10px] text-slate-400">
          Status enum tersedia: {Object.keys(meta.status_enums || {}).join(", ")}. Collection cadangan (belum ditulis Tahap 1): {(meta.collections_reserved || []).join(", ")}.
          <span className="ml-2 italic">Stage saat ini: {stageLabel}.</span>
        </div>
      )}
    </div>
  );
}
